import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import type { State } from "../redux/authReducer";

export type UserType = "Admin" | "User";

export type AuthorizationHookParameters = {
  /**
   * An array of user types that are authorized to access the resource.
   * @example ["Admin", "User"]
   */
  userTypes: UserType[];
  /**
   * The user ID of the resource being accessed if any or otherwise undefined.
   * @example "1234567890abcdef12345678"
   */
  resourceUserId?: string | undefined;
};

export type AuthorizationHookResult =
  | {
      authorized: true;
      token: string;
      userType: UserType;
      userId: string;
    }
  | {
      authorized: false;
      token: string;
      userType: UserType;
      userId: string;
    }
  | {
      authorized: false;
    };

/**
 * Custom hook to check if the user is authorized to access a resource based on user types and user ID.
 * @param params - The parameters containing user types and resource user ID.
 * @returns An object indicating whether the user is authorized and relevant user information.
 */
export function useAuthorization({
  userTypes,
  resourceUserId,
}: AuthorizationHookParameters): AuthorizationHookResult {
  const token = useSelector((state: State) => state.currentUser?.token);
  const userType = useSelector((state: State) => state.currentUser?.userType);
  const userId = useSelector((state: State) => state.currentUser?.id);
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      navigate("/");
    }
  }, [token, navigate]);

  const userTypeAllowed =
    typeof userType !== "undefined" && userTypes.includes(userType);

  return useMemo(() => {
    if (!token || !userType || !userId) {
      return { authorized: false };
    }

    if (!userTypeAllowed) {
      return { authorized: false, token, userType, userId };
    }

    if (userType === "User" && resourceUserId && userId !== resourceUserId) {
      return { authorized: false, token, userType, userId };
    }

    // If all checks pass, return authorized
    return { authorized: true, token, userType, userId };
  }, [token, userType, userId, userTypeAllowed, resourceUserId]);
}
